import { Client, ClientsAPI, ValidateSellerResponse } from 'core/clients';
import {
  PayloadAction,
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

import { CustomError } from 'core/errors';
import { Nullable } from 'utils/types';
import { RootState } from 'store/root-reducer';
import formatISO from 'date-fns/formatISO';
import { FlowOptions } from './flow-options.enum';

export type SearchClientParams = {
  documentType: string;
  documentNumber: string;
  validatorDigit?: string;
  entityId: string;
  isEmployee?: boolean;
  business_type?: string;
};

export type MainState = {
  search: {
    isLoading: boolean;
    error: Nullable<CustomError>;
    client: Nullable<Client>;
    params: SearchClientParams;
    created: string;
  };
  flow: string;
  isPlansSubmitted: boolean;
};

export type ParamsSeller = {
  entityId: string;
  documentNumber: string;
  documentType: string;
  business: string;
};

// ASYNC thunks
const validateSeller = createAsyncThunk<ValidateSellerResponse, ParamsSeller>(
  'main/validateSeller',
  async (params) => {
    try {
      return await ClientsAPI.getValidateSeller({ params });
    } catch (err) {
      return err;
    }
  }
);

// ASYNC thunks
const searchClient = createAsyncThunk<
  Client,
  SearchClientParams,
  { rejectValue: CustomError }
>('main/searchClient', async (params, { rejectWithValue }) => {
  try {
    return await ClientsAPI.getClientByDocument({ params });
  } catch (err) {
    const error: CustomError =
      err?.customError || CustomError.createClientError();
    return rejectWithValue(error);
  }
});

const initialState: MainState = {
  search: {
    client: null,
    params: { documentNumber: '', documentType: '', entityId: '' },
    error: null,
    isLoading: false,
    created: '',
  },
  flow: '',
  isPlansSubmitted: false,
};

const mainSlice = createSlice({
  name: 'main',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(searchClient.pending, (state, action) => {
      state.search.params = action.meta.arg;
      state.search.isLoading = true;
      state.search.error = null;
    });
    builder.addCase(searchClient.fulfilled, (state, action) => {
      state.search.isLoading = false;
      state.search.client = action.payload;
      state.search.error = null;
    });
    builder.addCase(searchClient.rejected, (state, { payload }) => {
      state.search.client = null;
      state.search.isLoading = false;
      state.search.error = payload;
    });
  },
  reducers: {
    setSearchParams: (
      state,
      action: PayloadAction<{
        documentNumber: string;
        documentType: string;
        entityId: string;
      }>
    ) => {
      state.search.params = action.payload;
    },
    flowSearchDocument: (state, action: PayloadAction<void>) => {
      state.flow = FlowOptions.SearchDocument;
      state.search.created = formatISO(new Date());
    },
    flowSearchAddress: (state, action: PayloadAction<void>) => {
      state.flow = FlowOptions.SearchAddress;
      state.search.created = formatISO(new Date());
    },
    setError: (state, action: PayloadAction<CustomError>) => {
      state.search.error = action.payload;
    },
    resetError: (state, action: PayloadAction<void>) => {
      state.search.error = null;
    },
    resetState: (state, action: PayloadAction<void>) => {
      return initialState;
    },
    setIsPlansSubmitted: (state, action: PayloadAction<void>) => {
      state.isPlansSubmitted = true;
    },
  },
});

const { reducer, actions } = mainSlice;

export const {
  resetState,
  flowSearchDocument,
  flowSearchAddress,
  resetError,
  setError,
  setSearchParams,
  setIsPlansSubmitted,
} = actions;

export { searchClient, validateSeller };

export const selectMainSearchClient = (state: RootState) => state.main.search;
export const selectSearchParams = (state: RootState) =>
  state.main.search.params;
export const selectSearchLoading = (state: RootState) =>
  state.main.search.isLoading;
export const selectSearchError = (state: RootState) => state.main.search.error;
export const selectFlow = (state: RootState) => state.main.flow;
export const selectSearchClient = (state: RootState) =>
  state.main.search.client;
export const selectSearchClientHasDebt = createSelector(
  [selectSearchClient],
  (client) => (client?.debt.amount || 0) > 0
);
export const selectSearchClientHasFraud = createSelector(
  [selectSearchClient],
  (client) => !!client?.debt.lineInformation
);
export const selectSearchClientMissingPersonalInfo = createSelector(
  [selectSearchClient],
  (client) => !client?.name || !client?.surname || !client?.birthDate
);

export default reducer;
